import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Drawer,
  Button,
  message,
  Spin,
  Pagination,
  Input,
  Select,
  Space,
  Divider,
  Typography,
  Upload,
  Statistic,
  Empty
} from 'antd'
import {
  EyeOutlined,
  SearchOutlined,
  EditOutlined,
  PlusOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  LockOutlined,
  WalletOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  HistoryOutlined,
  GiftOutlined,
  FilterOutlined,
  CrownOutlined,
  SlidersOutlined
} from '@ant-design/icons'
import { TiUserAdd } from 'react-icons/ti'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import { postTopupWithDuplicateConfirm } from '../utils/duplicateTopup'
import TopupSchemeKeyInput from '../components/TopupSchemeKeyInput'
import PurchaseConsentModal from '../components/PurchaseConsentModal'
import { PHOTO_URL } from '../utils/apiutils'
import {
  getCustomerDisplayData,
  getLineDisplayNameSuffix
} from '../utils/customerDisplay'
import {
  getCustomerAvatarSrc,
  getCustomerAvatarSrcForListCard
} from '../utils/customerAvatar'
import CustomerAvatarImg from '../components/common/CustomerAvatar'
import {
  Form,
  Radio,
  DatePicker,
  Modal,
  InputNumber,
  message as antdMessage
} from 'antd'
import dayjs from 'dayjs'
import BookingTable from '../components/BookingTable'
import { useNavigate, useSearchParams } from 'react-router-dom'
import imageCompression from 'browser-image-compression'
import heic2any from 'heic2any'
import { formatTz } from '../utils/timezone'
import { now as tzNow } from '../utils/timezone'

const { Text, Title } = Typography

// 顧客卡片樣式組件 - 與BookingTable.js保持一致
const CustomerCard = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e5e7;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #d5728a;
  }

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    padding: 14px 16px;
    gap: 0;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }
`

const CustomerCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media (max-width: 768px) {
    gap: 12px;
  }
`

const CustomerCardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
    gap: 12px;
  }
`

const CustomerAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f5f5f7;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgb(229, 151, 170), #c4596f);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 18px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`

const CustomerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;

  .customer-name {
    font-size: 15px;
    font-weight: 600;
    color: #1d1d1f;
    line-height: 1.2;
  }

  .customer-phone {
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .customer-name {
      font-size: 14px;
    }
    .customer-phone {
      font-size: 12px;
    }
  }
`

const CustomerInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 80px;
  width: 80px;
  flex-shrink: 0;
  text-align: center;

  @media (max-width: 768px) {
    min-width: auto;
    width: auto;
    flex: 1;
    text-align: left;
  }
`

/** 桌面版會員等級名稱可能較長，勿與其他 80px 欄共用寬度以免折行 */
const CustomerMembershipColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  min-width: max-content;
  max-width: 280px;
  text-align: center;

  @media (max-width: 768px) {
    min-width: auto;
    max-width: none;
    flex: 1;
    text-align: left;
  }
`

const CustomerInfoLabel = styled.span`
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 9px;
  }
`

const CustomerInfoValue = styled.span`
  font-size: 13px;
  color: #1d1d1f;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const CustomerTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 80px;
  max-width: 120px;
  flex-shrink: 0;

  .tag {
    background: #f0f0f0;
    color: #666;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    min-width: auto;
    max-width: none;
    justify-content: flex-start;
  }
`

const CustomerCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`

/* 桌面版卡片區塊（手機隱藏） */
const DesktopCardInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 768px) {
    display: none;
  }
`

/* 手機版卡片區塊（參考圖版：白卡、圓角、頭像左、資訊右） */
const MobileCardInner = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }
`

const MobileCardMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const MobileCardName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  line-height: 1.3;
`

const MobileCardMeta = styled.div`
  font-size: 12px;
  color: #8e8e93;
  line-height: 1.4;
`

const MobileCardTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
`

const MobileCardPoints = styled.span`
  font-size: 13px;
  color: #6e6e73;
`

const MobileCardTotalSpent = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #e67e22;
`

const MobileCardLastVisit = styled.div`
  font-size: 12px;
  color: #8e8e93;
`

const MobileCardStats = styled.div`
  font-size: 12px;
  color: #8e8e93;
`

const MobileBirthdayTag = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #ffb347 0%, #ffcc33 45%, #ffe066 100%);
  color: #fff;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 2px;
`

// 顧客卡片組件
const CustomerCardComponent = ({
  customer,
  onShowModal,
  membershipTiers = [],
  businessId,
  showRegion = false
}) => {
  const navigate = useNavigate()
  const [avatarError, setAvatarError] = useState(false)

  const handleCardClick = () => {
    navigate(`/customers/${customer._id}`)
  }

  const handleImageError = () => {
    setAvatarError(true)
  }

  const displayData = getCustomerDisplayData(customer, businessId)
  const membershipInfo = getMembershipDisplay(
    displayData.membershipLevel,
    membershipTiers
  )

  // 「當月」以店家時區判斷，否則月初／月底的深夜會差一個月
  const currentMonth = tzNow().month() + 1
  const birthdateMonth = displayData.birthdate
    ? dayjs(displayData.birthdate).month() + 1
    : 0
  const isBirthdayMonth = birthdateMonth === currentMonth

  const visitStats = customer.visitStats || {}
  const lastCompletedDate = visitStats.lastCompletedDate
  const completedBookings = visitStats.completedBookings ?? 0
  const totalBookings =
    visitStats.totalBookings ?? visitStats.completedBookings ?? 0
  const attendanceRate =
    visitStats.attendanceRate ??
    (totalBookings > 0
      ? Math.round((completedBookings / totalBookings) * 100)
      : 0)

  return (
    <CustomerCard onClick={handleCardClick}>
      <DesktopCardInner>
        <CustomerCardLeft>
          <CustomerAvatar>
            {getCustomerAvatarSrcForListCard(customer) && !avatarError ? (
              <img
                src={getCustomerAvatarSrcForListCard(customer)}
                alt={displayData.name || '未知'}
                onError={handleImageError}
              />
            ) : (
              <div className='avatar-placeholder'>
                {(displayData.name || '未')[0].toUpperCase()}
              </div>
            )}
          </CustomerAvatar>
          <CustomerInfo>
            <div className='customer-name'>{displayData.name}</div>
            <div className='customer-phone'>{displayData.phone}</div>
          </CustomerInfo>
        </CustomerCardLeft>

        <CustomerCardRight>
          <CustomerMembershipColumn>
            <CustomerInfoLabel>會員等級</CustomerInfoLabel>
            <CustomerInfoValue>
              <MembershipLevel color={membershipInfo.color}>
                {membershipInfo.text}
              </MembershipLevel>
            </CustomerInfoValue>
          </CustomerMembershipColumn>

          <CustomerInfoItem>
            <CustomerInfoLabel>最後到店</CustomerInfoLabel>
            <CustomerInfoValue>
              {lastCompletedDate
                ? dayjs(lastCompletedDate).format('MM/DD')
                : '未知'}
            </CustomerInfoValue>
          </CustomerInfoItem>

          <CustomerInfoItem>
            <CustomerInfoLabel>生日</CustomerInfoLabel>
            <CustomerInfoValue>
              {displayData.birthdate
                ? dayjs(displayData.birthdate).format('MM/DD')
                : '—'}
            </CustomerInfoValue>
          </CustomerInfoItem>

          {showRegion && (
            <CustomerInfoItem>
              <CustomerInfoLabel>地址</CustomerInfoLabel>
              <CustomerInfoValue title={displayData.region || ''}>
                {displayData.region || '—'}
              </CustomerInfoValue>
            </CustomerInfoItem>
          )}

          <CustomerTags>
            {customer.tags && customer.tags.length > 0 ? (
              customer.tags.slice(0, 2).map((tag, index) => (
                <span key={index} className='tag'>
                  {tag}
                </span>
              ))
            ) : (
              <span className='tag'>無標籤</span>
            )}
          </CustomerTags>
        </CustomerCardRight>
      </DesktopCardInner>

      <MobileCardInner>
        <CustomerAvatar>
          {getCustomerAvatarSrcForListCard(customer) && !avatarError ? (
            <img
              src={getCustomerAvatarSrcForListCard(customer)}
              alt={displayData.name}
              onError={handleImageError}
            />
          ) : (
            <div className='avatar-placeholder'>
              {(displayData.name || '未')[0].toUpperCase()}
            </div>
          )}
        </CustomerAvatar>
        <MobileCardMain>
          <MobileCardName>{displayData.name}</MobileCardName>
          <MobileCardMeta>{displayData.phone}</MobileCardMeta>
          <MobileCardMeta>
            {displayData.email
              ? displayData.email.length > 30
                ? displayData.email.slice(0, 30) + '...'
                : displayData.email
              : '—'}
          </MobileCardMeta>
          <MobileCardTagRow>
            <MembershipLevel color={membershipInfo.color}>
              {membershipInfo.text}
            </MembershipLevel>
            <MobileCardTotalSpent>
              總消費 $ {Number(displayData.totalSpent || 0).toLocaleString()}
            </MobileCardTotalSpent>
          </MobileCardTagRow>
          {showRegion && displayData.region && (
            <MobileCardMeta>{displayData.region}</MobileCardMeta>
          )}
          <MobileCardLastVisit>
            最後到店:{' '}
            {lastCompletedDate
              ? dayjs(lastCompletedDate).format('YYYY/MM/DD')
              : '—'}
          </MobileCardLastVisit>
          <MobileCardStats>
            預約 {totalBookings} 次 | 完成 {completedBookings} 次 | 出席率{' '}
            {attendanceRate}%
          </MobileCardStats>
          {isBirthdayMonth && (
            <MobileBirthdayTag>
              <GiftOutlined style={{ marginRight: 4 }} />
              當月壽星
            </MobileBirthdayTag>
          )}
        </MobileCardMain>
      </MobileCardInner>
    </CustomerCard>
  )
}

// 定義斷點
const breakpoints = {
  small: '576px',
  medium: '820px',
  large: '992px',
  xlarge: '1200px'
}

// 性別轉換函數
const translateGender = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'female':
      return '女'
    case 'male':
      return '男'
    default:
      return '未知性別'
  }
}

// 修改會員等級顏色映射
const membershipColors = {
  普通會員: 'linear-gradient(135deg, #ca9f71, #9c5e1c)',
  銀卡會員: 'linear-gradient(135deg, #e0e0e0, #c0c0c0)',
  金卡會員: 'linear-gradient(135deg, #ffd700, #ffaa00)',
  鑽石會員: 'linear-gradient(135deg, #b9f2ff, #00bfff)',
  VIP會員: 'linear-gradient(135deg, #ff69b4, #ff1493)'
}

// 修改會員等級顯示組件
const MembershipLevel = styled.span`
  background: ${(props) => props.color};
  color: ${(props) => (props.color.includes('#ffd700') ? '#000' : '#fff')};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  display: inline-block;
  white-space: nowrap;
`

// 會員等級轉換函數（支持舊的英文名稱和新的自定義名稱）
const getMembershipDisplay = (level, membershipTiers = []) => {
  if (!level) {
    return {
      text: '普通會員',
      color: membershipColors['普通會員']
    }
  }

  // 1. 優先：商家現有等級表（直接命中名稱）
  //    避免把店家自訂的「VIP」誤判為舊版翻譯而變成「VIP會員」
  const lowerLevel = level.toLowerCase()
  const directMatch = membershipTiers.find(
    (tier) => tier.name === level || tier.name.toLowerCase() === lowerLevel
  )
  if (directMatch) {
    return {
      text: directMatch.name,
      color: directMatch.color || membershipColors['普通會員']
    }
  }

  // 2. fallback：舊版英文預設名稱對應（regular/silver/gold/diamond/vip）
  const oldEnglishNames = {
    regular: '普通會員',
    silver: '銀卡會員',
    gold: '金卡會員',
    diamond: '鑽石會員',
    vip: 'VIP會員'
  }
  if (oldEnglishNames[lowerLevel]) {
    const chineseLevel = oldEnglishNames[lowerLevel]
    return {
      text: chineseLevel,
      color: membershipColors[chineseLevel]
    }
  }

  // 如果都找不到，返回原值作為顯示文字，使用默認顏色
  return {
    text: level,
    color: membershipColors['普通會員']
  }
}

const CustomerDetailsWrapper = styled.div`
  padding: 24px;
  height: calc(100vh - 180px);
  overflow-y: auto;
  position: relative;

  @media (min-width: 768px) and (max-width: 1024px) {
    padding: 16px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    .avatar-section {
      grid-column: 1 / -1;
    }

    .stats-section {
      grid-column: 1 / -1;
    }

    .info-section {
      grid-column: 1 / 2;
    }

    .additional-info {
      grid-column: 2 / 3;
    }
  }
`

// 添加新的樣式組件
const ViewBookingsButton = styled(Button)`
  margin-top: 16px;
  width: 100%;
  background-color: #d5728a;
  border-color: #d5728a;
  &:hover {
    background-color: #c45f77;
    border-color: #c45f77;
  }
`

// 添加提示文字的樣式
const TagHelperText = styled.div`
  color: #666;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 8px;
  font-style: italic;
`

const StatCard = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  text-align: center;

  .stat-title {
    color: #8c8c8c;
    font-size: 14px;
    margin-bottom: 8px;
  }

  .stat-value {
    font-size: 24px;
    font-weight: bold;
    color: #333;
  }

  @media (max-width: 820px) {
    .stat-value {
      font-size: 20px;
    }
  }
`

const PhotoWall = styled.div`
  margin: 16px 0 16px 0;
  .ant-upload-list-picture-card {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 8px;
  }
  .ant-upload-list-picture-card-container {
    width: 100% !important;
    height: 100px !important;
  }
`

const ServiceList = styled.div`
  margin-top: 16px;
  .service-item {
    background: #fafafa;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`

const StatsSection = styled.div`
  margin: 24px 0;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (min-width: 768px) and (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (min-width: 1025px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
`

const StatItem = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  text-align: center;

  .ant-statistic {
    .ant-statistic-title {
      color: #8c8c8c;
      font-size: 14px;
      margin-bottom: 8px;
    }

    .ant-statistic-content {
      font-size: 20px;
      color: #333;

      .ant-statistic-content-prefix {
        margin-right: 4px;
      }
    }
  }
`

// 將 displayTags 移到組件外作為通用函數
const displayTags = (tags) => {
  if (!tags) return []
  if (Array.isArray(tags)) return tags
  try {
    return JSON.parse(tags)
  } catch {
    return typeof tags === 'string' ? [tags] : []
  }
}

// 新增樣式組件
const PhotoMask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  text-align: center;
  padding: 20px;
  z-index: 2;
`

const UpgradeText = styled(Typography.Text)`
  color: white;
  font-size: 16px;
  margin-bottom: 12px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`

const UpgradeButton = styled(Button)`
  background: linear-gradient(135deg, #d5728a, #c45f77);
  border: none;
  &:hover {
    background: linear-gradient(135deg, #c45f77, #b54d65);
  }
`

// 進階搜索初始值（定義在 component 外避免每次渲染重建）
const emptyAdvancedFilters = {
  tags: [],
  gender: '',
  birthdayMonth: null,
  membershipLevel: '',
  lastVisitBeforeDays: null
}

const Customers = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [visible, setVisible] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const { businessId, user } = useAuth()
  const plan = user.plan
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCustomers, setTotalCustomers] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  // 已「送出」的關鍵字。與輸入框的 searchTerm 分開，是為了讓打字／刪字
  // 完全不影響列表 —— 只有按 Enter 或「搜索」鈕才會變動這個值。
  const [activeKeyword, setActiveKeyword] = useState('')
  const [filterType, setFilterType] = useState('all')
  // 會員等級篩選（'' 表示未篩選；其他值為 tier name）
  const [tierFilter, setTierFilter] = useState('')
  const [tierFilterModalOpen, setTierFilterModalOpen] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)

  // 進階搜索
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false)
  const [advancedFilters, setAdvancedFilters] = useState(emptyAdvancedFilters)
  const [advancedForm] = Form.useForm()
  const pageSize = 10
  const [isEditing, setIsEditing] = useState(false)
  const [editedCustomer, setEditedCustomer] = useState(null)
  // 地址欄位預設不顯示（地址字串長、會把卡片撐開），由店家自己勾選，選擇記在瀏覽器
  const [showRegion, setShowRegion] = useState(
    () => localStorage.getItem('customersShowRegion') === '1'
  )
  const [addDrawerVisible, setAddDrawerVisible] = useState(false)
  const [addCustomerForm] = Form.useForm()
  const [bookingsVisible, setBookingsVisible] = useState(false)
  const [loadingBookings, setLoadingBookings] = useState(false)
  const [customerBookings, setCustomerBookings] = useState(null)
  const [bookingsLoading, setBookingsLoading] = useState(false)
  const [tagOptions, setTagOptions] = useState([
    '遲到',
    '價格敏感',
    '過敏',
    '加強關懷'
  ])
  const [customerPhotos, setCustomerPhotos] = useState([])
  const [showBookingDrawer, setShowBookingDrawer] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [membershipTiers, setMembershipTiers] = useState([])
  const [membershipTiersLoading, setMembershipTiersLoading] = useState(false)

  // 儲值金相關狀態
  const [balanceInfo, setBalanceInfo] = useState(null)
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [transactionHistory, setTransactionHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [balanceModalVisible, setBalanceModalVisible] = useState(false)
  // 儲值金操作送出中：state 給確認鈕的 loading，ref 給送出前的同步判斷（不受 closure 影響）
  const [balanceSubmitting, setBalanceSubmitting] = useState(false)
  const balanceSubmittingRef = useRef(false)
  const [balanceOperation, setBalanceOperation] = useState('') // 'topup', 'deduct', 'refund', 'adjust'
  const [balanceForm] = Form.useForm()
  const [balanceConsent, setBalanceConsent] = useState({
    open: false,
    customerId: null,
    customerName: '',
    variables: {},
    pendingValues: null
  })
  const [adjustmentType, setAdjustmentType] = useState('increase') // 'increase' 或 'decrease'
  const [employees, setEmployees] = useState([]) // 員工列表（儲值時選銷售人員用）

  console.log('user', user)

  // 獲取會員等級列表
  const fetchMembershipTiers = async () => {
    if (!businessId) return

    setMembershipTiersLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/membership-tiers`
      )
      if (response.data.success) {
        const tiersData = response.data.data?.tiers || []
        const enabledTiers = tiersData.filter((t) => t.isEnabled !== false)
        setMembershipTiers(enabledTiers)
      }
    } catch (error) {
      console.error('獲取會員等級列表失敗:', error)
    } finally {
      setMembershipTiersLoading(false)
    }
  }

  useEffect(() => {
    if (selectedCustomer) {
      const photos =
        selectedCustomer.photos ||
        selectedCustomer.businessSpecificData?.photos ||
        []
      setCustomerPhotos(
        photos.map((photo, index) => ({
          uid: photo._id,
          name: `photo-${index}`,
          status: 'done',
          url: `${PHOTO_URL}${photo.url}`
        }))
      )
    }
  }, [selectedCustomer])

  const fetchEmployees = async () => {
    if (!businessId) return
    try {
      const response = await api.get(`/api/employees/${businessId}`)
      setEmployees(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }

  useEffect(() => {
    if (businessId) {
      fetchMembershipTiers()
      fetchEmployees()
    }
  }, [businessId])

  // 從 URL 同步 page、keyword、filter（含返回上一頁）
  useEffect(() => {
    const page = parseInt(searchParams.get('page'), 10)
    const keyword = searchParams.get('keyword') || ''
    const filter = searchParams.get('filter') || 'all'
    const tier = searchParams.get('tier') || ''
    if (page && !isNaN(page) && page >= 1) setCurrentPage(page)
    setSearchTerm(keyword)
    if (tier) {
      setFilterType('tier')
      setTierFilter(tier)
    } else if (filter === 'birthday') {
      setFilterType('birthday')
      setTierFilter('')
    } else {
      setFilterType('all')
      setTierFilter('')
    }
  }, [searchParams])

  // 當 URL 有 keyword（含返回上一頁）時，還原搜尋結果
  const skipUrlSearchRef = useRef(false)
  useEffect(() => {
    const keyword = searchParams.get('keyword') || ''
    if (!keyword.trim() || keyword.trim().length < 2 || !businessId) return
    if (skipUrlSearchRef.current) {
      skipUrlSearchRef.current = false
      return
    }
    const runSearch = async () => {
      setSearchLoading(true)
      try {
        const response = await api.get(
          `/api/business_B/${businessId}/customers/search`,
          { params: { searchTerm: keyword.trim() } }
        )
        if (response.data.success) {
          const processed = (response.data.data || []).map((c) => ({
            ...c,
            tags: c.tags
              ? typeof c.tags === 'string'
                ? JSON.parse(c.tags)
                : c.tags
              : []
          }))
          setCustomers(processed)
          setTotalCustomers(processed.length)
          setCurrentPage(1)
          setSearchTerm(keyword.trim())
          setActiveKeyword(keyword.trim())
          setFilterType('all')
        } else {
          setCustomers([])
          setTotalCustomers(0)
        }
      } catch (err) {
        console.error('還原搜尋失敗:', err)
      } finally {
        setSearchLoading(false)
      }
    }
    runSearch()
  }, [searchParams, businessId])

  // 將 page、keyword、filter 寫入 URL
  const syncParamsToUrl = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (updates.page != null) {
            if (updates.page === 1) next.delete('page')
            else next.set('page', String(updates.page))
          }
          if (updates.keyword != null) {
            if (!updates.keyword) next.delete('keyword')
            else next.set('keyword', updates.keyword)
          }
          if (updates.filter != null) {
            if (updates.filter === 'all') next.delete('filter')
            else next.set('filter', updates.filter)
          }
          if (updates.tier !== undefined) {
            if (!updates.tier) next.delete('tier')
            else next.set('tier', updates.tier)
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const fetchAllCustomers = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/customers`,
        {
          params: {
            page: currentPage,
            limit: pageSize
          }
        }
      )
      // 處理響應數據
      const processedCustomers = response.data.data.map((customer) => ({
        ...customer,
        tags: customer.tags
          ? typeof customer.tags === 'string'
            ? JSON.parse(customer.tags)
            : customer.tags
          : []
      }))
      setCustomers(processedCustomers)
      setTotalCustomers(response.data.pagination.totalCustomers)
    } catch (error) {
      console.error('獲取客戶信息失敗:', error)
      message.error('獲取客戶信息失敗，請稍後再試')
    } finally {
      setLoading(false)
    }
  }, [businessId, currentPage, pageSize])

  // 依會員等級篩選
  const fetchTierCustomers = useCallback(async () => {
    if (!tierFilter) return
    setLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/customers`,
        {
          params: {
            page: currentPage,
            limit: pageSize,
            membershipLevel: tierFilter
          }
        }
      )
      const processedCustomers = (response.data.data || []).map((customer) => ({
        ...customer,
        tags: customer.tags
          ? typeof customer.tags === 'string'
            ? JSON.parse(customer.tags)
            : customer.tags
          : []
      }))
      setCustomers(processedCustomers)
      setTotalCustomers(
        response.data.pagination?.totalCustomers ?? processedCustomers.length
      )
    } catch (error) {
      console.error('依會員等級取得客戶失敗:', error)
      message.error('依會員等級篩選失敗，請稍後再試')
      setCustomers([])
      setTotalCustomers(0)
    } finally {
      setLoading(false)
    }
  }, [businessId, currentPage, pageSize, tierFilter])

  // 當月壽星：取得生日在當月的顧客（僅依月份判斷，後端需支援 birthdayMonth 查詢參數）
  const fetchBirthdayCustomers = useCallback(async () => {
    setLoading(true)
    try {
      const birthdayMonth = tzNow().month() + 1 // 1–12
      const response = await api.get(
        `/api/business_B/${businessId}/customers`,
        {
          params: {
            page: currentPage,
            limit: pageSize,
            birthdayMonth
          }
        }
      )
      const processedCustomers = (response.data.data || []).map((customer) => ({
        ...customer,
        tags: customer.tags
          ? typeof customer.tags === 'string'
            ? JSON.parse(customer.tags)
            : customer.tags
          : []
      }))
      setCustomers(processedCustomers)
      const total =
        response.data.pagination?.totalCustomers ?? processedCustomers.length
      setTotalCustomers(total)
    } catch (error) {
      console.error('獲取當月壽星失敗:', error)
      message.error('獲取當月壽星失敗，請稍後再試')
      setCustomers([])
      setTotalCustomers(0)
    } finally {
      setLoading(false)
    }
  }, [businessId, currentPage, pageSize])

  // 進階搜索：傳入所有進階篩選條件
  const fetchAdvancedCustomers = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page: currentPage, limit: pageSize }
      if (advancedFilters.gender) params.gender = advancedFilters.gender
      if (advancedFilters.tags?.length > 0) params.tags = advancedFilters.tags.join(',')
      if (advancedFilters.birthdayMonth) params.birthdayMonth = advancedFilters.birthdayMonth
      if (advancedFilters.membershipLevel) params.membershipLevel = advancedFilters.membershipLevel
      if (advancedFilters.lastVisitBeforeDays) params.lastVisitBeforeDays = advancedFilters.lastVisitBeforeDays
      const response = await api.get(`/api/business_B/${businessId}/customers`, { params })
      const processed = (response.data.data || []).map((c) => ({
        ...c,
        tags: c.tags ? (typeof c.tags === 'string' ? JSON.parse(c.tags) : c.tags) : []
      }))
      setCustomers(processed)
      setTotalCustomers(response.data.pagination?.totalCustomers ?? processed.length)
    } catch (error) {
      console.error('進階搜索失敗:', error)
      message.error('進階搜索失敗，請稍後再試')
      setCustomers([])
      setTotalCustomers(0)
    } finally {
      setLoading(false)
    }
  }, [businessId, currentPage, pageSize, advancedFilters])

  useEffect(() => {
    if (filterType === 'advanced') {
      fetchAdvancedCustomers()
    } else if (filterType === 'tier' && tierFilter) {
      fetchTierCustomers()
    } else if (filterType === 'birthday') {
      fetchBirthdayCustomers()
    } else if (filterType === 'all' && !activeKeyword) {
      fetchAllCustomers()
    }
    // 注意：這裡刻意「不」依賴 searchTerm（輸入框內容）。
    // 依賴它的話，使用者每刪一個字、刪到空的瞬間就會重新抓一次全部客戶，
    // 等於沒按送出也在動列表。列表只在「已送出的關鍵字」或篩選條件變動時才更新。
  }, [
    currentPage,
    filterType,
    tierFilter,
    activeKeyword,
    fetchAllCustomers,
    fetchBirthdayCustomers,
    fetchTierCustomers,
    fetchAdvancedCustomers
  ])

  const handleSearch = async (searchValue) => {
    // 如果沒有傳入參數，使用當前的 searchTerm 狀態
    // 確保 searchValue 是字符串，如果是事件對象則忽略
    let searchStr = searchTerm // 默認使用狀態中的值

    if (searchValue !== undefined && searchValue !== null) {
      // 如果是事件對象，從 e.target.value 獲取
      if (typeof searchValue === 'object' && searchValue.target) {
        searchStr = searchValue.target.value || ''
      } else if (typeof searchValue === 'string') {
        searchStr = searchValue
      } else {
        // 其他情況，嘗試轉換為字符串
        searchStr = String(searchValue || '')
      }
    }

    const trimmedValue = searchStr.trim()

    console.log('搜索觸發:', {
      searchValue,
      searchTerm,
      searchStr,
      trimmedValue
    })

    if (!trimmedValue || trimmedValue.length === 0) {
      // 如果搜索内容为空，重置为显示全部客户
      handleFilterChange()
      return
    }


    setSearchLoading(true)
    try {
      console.log('發送搜索請求:', { searchTerm: trimmedValue, businessId })
      const response = await api.get(
        `/api/business_B/${businessId}/customers/search`,
        {
          params: { searchTerm: trimmedValue }
        }
      )
      console.log('搜索響應:', response.data)
      if (response.data.success) {
        setCustomers(response.data.data)
        setTotalCustomers(response.data.data.length)
        setCurrentPage(1)
        setActiveKeyword(trimmedValue)
        skipUrlSearchRef.current = true // 避免 URL 更新後重複搜尋
        syncParamsToUrl({ page: 1, keyword: trimmedValue, filter: 'all' })
        message.success(`找到 ${response.data.data.length} 個客戶`)
      } else {
        setCustomers([])
        setTotalCustomers(0)
        message.info('未找到匹配的客戶')
      }
    } catch (error) {
      console.error('搜索失敗:', error)
      message.error('搜索失敗，請稍後再試')
    } finally {
      setSearchLoading(false)
    }
  }

  // 處理搜索輸入變化：純粹更新輸入框文字。
  // 打字、刪字、按叉叉清空都不會動到列表 —— 一律要按 Enter 或「搜索」鈕才送出。
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    syncParamsToUrl({ page })
  }

  const showDrawer = (customer) => {
    setSelectedCustomer(customer)
    setVisible(true)
  }

  const onClose = () => {
    setVisible(false)
    setIsEditing(false)
    setEditedCustomer(null)
    // 依目前篩選類型重新載入列表
    if (filterType === 'birthday') {
      fetchBirthdayCustomers()
    } else {
      fetchAllCustomers()
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
    // 創建一個深拷貝以避免直接引用
    const customerCopy = JSON.parse(JSON.stringify(selectedCustomer))
    // 確保 businessSpecificData 存在
    if (!customerCopy.businessSpecificData) {
      customerCopy.businessSpecificData = {}
    }
    // 初始化編輯資料，使用顯示資料作為初始值
    const displayData = getCustomerDisplayData(customerCopy, businessId)
    customerCopy.businessSpecificData = {
      ...customerCopy.businessSpecificData,
      name: displayData.name !== '未知' ? displayData.name : '',
      phone: displayData.phone !== '未提供' ? displayData.phone : '',
      email: displayData.email !== '未知' ? displayData.email : '',
      gender: displayData.gender || '',
      birthdate: displayData.birthdate || '',
      region: displayData.region || '',
      notes: displayData.notes || '',
      tags: displayData.tags || [],
      membershipLevel: displayData.membershipLevel || 'Regular',
      points: displayData.points || 0,
      totalSpent: displayData.totalSpent || 0,
      blacklist: displayData.blacklist || false
    }
    setEditedCustomer(customerCopy)
  }

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleDelete = async (customerId) => {
    Modal.confirm({
      title: '確認刪除客戶',
      content: (
        <div>
          <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>
            確定要刪除此客戶嗎？
          </p>
          <p style={{ color: '#ff4d4f', fontSize: '13px' }}>
            ⚠️ 此操作不可復原，客戶將從列表中移除。
          </p>
        </div>
      ),
      okText: '確認刪除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      width: 400,
      onOk: async () => {
        try {
          const response = await api.delete(
            `/api/business_B/${businessId}/customers/${customerId}`
          )
          if (response.data.success) {
            message.success('客戶已成功刪除')
            // 關閉抽屜
            setVisible(false)
            setIsEditing(false)
            setSelectedCustomer(null)
            // 重新獲取客戶列表
            fetchAllCustomers()
          } else {
            message.error(response.data.error || '刪除失敗')
          }
        } catch (error) {
          console.error('刪除客戶失敗:', error)
          const errorMessage =
            error.response?.data?.error || '刪除客戶失敗，請稍後再試'
          message.error(errorMessage)
        }
      }
    })
  }

  const handleSave = async () => {
    try {
      setIsUploading(true)

      // 驗證姓名是否填寫（使用 businessSpecificData）
      const businessData = editedCustomer.businessSpecificData || {}
      if (!businessData.name?.trim()) {
        message.error('請輸入姓名')
        setIsUploading(false)
        return
      }

      // 確保 tags 是數組格式
      let tags = []
      if (businessData.tags) {
        if (Array.isArray(businessData.tags)) {
          tags = businessData.tags
        } else if (typeof businessData.tags === 'string') {
          tags = businessData.tags.split(',').map((tag) => tag.trim())
        }
      }

      // 構建請求數據（所有資料都保存到 businessSpecificData）
      const requestData = {
        action: 'manualUpdate',
        name: businessData.name?.trim() || '',
        phone: businessData.phone || '',
        email: businessData.email || '',
        gender: businessData.gender || '',
        birthdate: businessData.birthdate || '',
        region: businessData.region || '',
        tags: tags,
        notes: businessData.notes || '',
        membershipLevel: businessData.membershipLevel || 'Regular',
        // 點數不從編輯表單送：後端已不接受，一律走紅利點數面板（有異動紀錄）
        totalSpent: businessData.totalSpent || 0,
        blacklist: businessData.blacklist || false
      }

      // 處理照片相關邏輯
      const originalPhotos =
        selectedCustomer.photos ||
        selectedCustomer.businessSpecificData?.photos ||
        []
      const originalPhotoIds =
        originalPhotos.map((p) => p._id).filter(Boolean) || []
      const currentPhotoIds = customerPhotos
        .filter((p) => p.url)
        .map((p) => p.uid)
      const deletedPhotoIds = originalPhotoIds.filter(
        (id) => !currentPhotoIds.includes(id)
      )

      const hasNewPhotos = customerPhotos.some((p) => p.originFileObj)
      const hasDeletedPhotos = deletedPhotoIds.length > 0
      const hasPhotos = hasNewPhotos || hasDeletedPhotos

      let response

      if (hasPhotos) {
        // 有照片時：使用 FormData 格式
        const formData = new FormData()

        // 將基本數據添加到 FormData
        Object.keys(requestData).forEach((key) => {
          if (key === 'tags') {
            formData.append('tags', JSON.stringify(tags))
          } else if (key === 'blacklist') {
            formData.append('blacklist', requestData.blacklist === true)
          } else if (
            requestData[key] !== undefined &&
            requestData[key] !== null &&
            requestData[key] !== ''
          ) {
            formData.append(key, requestData[key])
          }
        })

        // 添加刪除的照片
        if (hasDeletedPhotos) {
          formData.append('deletedPhotos', JSON.stringify(deletedPhotoIds))
        }

        // 添加新照片
        customerPhotos
          .filter((p) => p.originFileObj)
          .forEach((p) => {
            formData.append('photos', p.originFileObj)
          })

        // 處理照片上傳
        if (hasNewPhotos) {
          message.loading({
            content: '正在處理照片，請稍候...',
            key: 'photoUpload',
            duration: 0
          })
        }

        response = await api.put(
          `/api/business_B/${businessId}/customers/${editedCustomer._id}`,
          formData
        )
      } else {
        // 沒有照片時：使用 JSON 格式
        response = await api.put(
          `/api/business_B/${businessId}/customers/${editedCustomer._id}`,
          requestData,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
      }

      if (response.data.success) {
        if (hasNewPhotos) {
          message.success({
            content: '照片上傳成功！',
            key: 'photoUpload',
            duration: 2
          })
        }
        message.success('客戶資料更新成功')

        // 創建更新後的客戶資料，確保包含所有編輯的欄位
        const responseData = response.data.data || response.data.customer || {}
        const updatedCustomer = {
          ...selectedCustomer,
          ...responseData,
          businessSpecificData: {
            ...selectedCustomer.businessSpecificData,
            ...responseData.businessSpecificData,
            ...businessData
          },
          photos:
            responseData.photos ||
            responseData.businessSpecificData?.photos ||
            selectedCustomer.photos ||
            [],
          tags: Array.isArray(responseData.tags)
            ? responseData.tags
            : displayTags(
                responseData.tags || responseData.businessSpecificData?.tags
              )
        }

        // 更新 selectedCustomer 狀態
        setSelectedCustomer(updatedCustomer)

        // 更新照片列表
        const updatedPhotos =
          updatedCustomer.photos ||
          updatedCustomer.businessSpecificData?.photos ||
          []
        setCustomerPhotos(
          updatedPhotos.map((photo, index) => ({
            uid: photo._id,
            name: `photo-${index}`,
            status: 'done',
            url: `${PHOTO_URL}${photo.url}`
          }))
        )

        // 更新客戶列表中的對應客戶資料
        setCustomers((prevCustomers) =>
          prevCustomers.map((customer) =>
            customer._id === editedCustomer._id ? updatedCustomer : customer
          )
        )

        setEditedCustomer(null)
        setIsEditing(false)
      } else {
        message.error('更新失敗，請稍後再試')
      }
    } catch (error) {
      console.error('更新客戶資料失敗:', error)
      message.error('更新失敗，請稍後再試')
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedCustomer(null)
  }

  const handleFilterChange = () => {
    setFilterType('all')
    setTierFilter('')
    setCurrentPage(1)
    setSearchTerm('')
    setActiveKeyword('')
    setAdvancedFilters(emptyAdvancedFilters)
    syncParamsToUrl({ page: 1, keyword: '', filter: 'all', tier: '' })
    fetchAllCustomers()
  }

  // 計算目前已套用的進階篩選數量
  const activeAdvancedCount = [
    advancedFilters.tags?.length > 0,
    !!advancedFilters.gender,
    !!advancedFilters.birthdayMonth,
    !!advancedFilters.membershipLevel,
    !!advancedFilters.lastVisitBeforeDays
  ].filter(Boolean).length

  const openAdvancedSearch = () => {
    // 開啟時帶入目前已套用的條件
    advancedForm.setFieldsValue({
      tags: advancedFilters.tags || [],
      gender: advancedFilters.gender || '',
      birthdayMonth: advancedFilters.birthdayMonth || null,
      membershipLevel: advancedFilters.membershipLevel || '',
      lastVisitBeforeDays: advancedFilters.lastVisitBeforeDays || null
    })
    setAdvancedSearchOpen(true)
  }

  const handleApplyAdvancedFilters = (values) => {
    const filters = {
      tags: values.tags || [],
      gender: values.gender || '',
      birthdayMonth: values.birthdayMonth || null,
      membershipLevel: values.membershipLevel || '',
      lastVisitBeforeDays: values.lastVisitBeforeDays || null
    }
    setAdvancedFilters(filters)
    setFilterType('advanced')
    setTierFilter('')
    setCurrentPage(1)
    setSearchTerm('')
    setActiveKeyword('')
    syncParamsToUrl({ page: 1, keyword: '', filter: 'advanced', tier: '' })
    setAdvancedSearchOpen(false)
  }

  const handleClearAdvancedFilters = () => {
    advancedForm.resetFields()
    setAdvancedFilters(emptyAdvancedFilters)
    setFilterType('all')
    setCurrentPage(1)
    syncParamsToUrl({ page: 1, filter: 'all', tier: '' })
    setAdvancedSearchOpen(false)
  }

  const handleBirthdayFilter = () => {
    setFilterType('birthday')
    setTierFilter('')
    setCurrentPage(1)
    setSearchTerm('')
    setActiveKeyword('')
    syncParamsToUrl({ page: 1, keyword: '', filter: 'birthday', tier: '' })
    // 由 useEffect 依 filterType 觸發 fetchBirthdayCustomers
  }

  const handleApplyTierFilter = (tierName) => {
    if (!tierName) return
    setFilterType('tier')
    setTierFilter(tierName)
    setCurrentPage(1)
    setSearchTerm('')
    setActiveKeyword('')
    setTierFilterModalOpen(false)
    syncParamsToUrl({
      page: 1,
      keyword: '',
      filter: 'all',
      tier: tierName
    })
    // 由 useEffect 依 filterType + tierFilter 觸發 fetchTierCustomers
  }

  const handleClearTierFilter = () => {
    setTierFilter('')
    setFilterType('all')
    setCurrentPage(1)
    syncParamsToUrl({ page: 1, filter: 'all', tier: '' })
  }

  const handleAddCustomer = async (values) => {
    try {
      const response = await api.post(
        `/api/business_B/${businessId}/customers`,
        {
          ...values,
          birthdate: values.birthdate?.format('YYYY-MM-DD')
        }
      )

      if (response.data.success) {
        message.success('顧客新增成功')
        setAddDrawerVisible(false)
        addCustomerForm.resetFields()
        fetchAllCustomers() // 重新獲取客戶列表
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || '新增顧客失敗'
      message.error(errorMessage)
    }
  }

  const handleViewBookings = async (customerId, page = 1) => {
    setLoadingBookings(true)
    try {
      const response = await api.get(
        `/api/business/bookings/customer/${customerId}`,
        {
          params: {
            page,
            limit: 15
          }
        }
      )
      setCustomerBookings(response.data)
      setBookingsVisible(true)
    } catch (error) {
      console.error('獲取預約記錄失敗:', error)
    } finally {
      setLoadingBookings(false)
    }
  }

  const handlePhotoChange = async ({ fileList: newFileList }) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    }

    try {
      // 更新檔案列表，設置新上傳的檔案為 uploading 狀態
      const updatedFileList = newFileList.map((file) => {
        if (file.originFileObj && !file.status) {
          return { ...file, status: 'uploading' }
        }
        return file
      })
      setCustomerPhotos(updatedFileList)

      // 處理新上傳的檔案
      const processedFileList = await Promise.all(
        newFileList.map(async (fileItem) => {
          // 如果是已存在的照片，直接返回
          if (!fileItem.originFileObj) {
            return fileItem
          }

          const file = fileItem.originFileObj
          const isHeicHeif = /\.(heic|heif)$/i.test(file.name)

          try {
            if (isHeicHeif) {
              const jpegBlob = await heic2any({
                blob: file,
                toType: 'image/jpeg',
                quality: 0.8
              })

              const baseName = file.name.replace(/\.(heic|heif)$/i, '')
              const newFileName = `${baseName}.jpg`

              const convertedFile = new File([jpegBlob], newFileName, {
                type: 'image/jpeg',
                lastModified: new Date().getTime()
              })

              const compressedFile = await imageCompression(
                convertedFile,
                options
              )

              return {
                ...fileItem,
                originFileObj: new File([compressedFile], newFileName, {
                  type: 'image/jpeg',
                  lastModified: new Date().getTime()
                }),
                status: 'done'
              }
            } else {
              const compressedFile = await imageCompression(file, options)
              return {
                ...fileItem,
                originFileObj: new File([compressedFile], file.name, {
                  type: compressedFile.type,
                  lastModified: new Date().getTime()
                }),
                status: 'done'
              }
            }
          } catch (error) {
            console.error('照片處理失敗:', error)
            return {
              ...fileItem,
              status: 'error',
              error: '照片處理失敗'
            }
          }
        })
      )

      setCustomerPhotos(processedFileList)
    } catch (error) {
      console.error('照片處理失敗:', error)
      message.error('照片處理失敗，請稍後再試')
    }
  }

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/customers/${customerId}`
      )
      if (response.data.success) {
        const customerData = response.data.data
        setSelectedCustomer(customerData)
        // 更新客戶列表中的對應客戶資料
        setCustomers((prevCustomers) =>
          prevCustomers.map((customer) =>
            customer._id === customerId ? customerData : customer
          )
        )
      }
    } catch (error) {
      console.error('獲取客戶詳情失敗:', error)
      message.error('獲取客戶詳情失敗')
    }
  }

  const fetchCustomerBookings = async (customerId, page = 1) => {
    if (!customerId) return

    setBookingsLoading(true)
    try {
      const response = await api.get(
        `/api/business/bookings/customer/${customerId}`,
        {
          params: {
            page,
            limit: 15
          }
        }
      )
      setCustomerBookings(response.data)
    } catch (error) {
      console.error('獲取客戶預約失敗:', error)
    } finally {
      setBookingsLoading(false)
    }
  }

  // 當選擇客戶時自動獲取預約記錄、餘額和交易歷史
  useEffect(() => {
    if (selectedCustomer?._id) {
      fetchCustomerBookings(selectedCustomer._id)
      fetchCustomerBalance(selectedCustomer._id)
      fetchTransactionHistory(selectedCustomer._id)
    }
  }, [selectedCustomer])

  // 獲取客戶儲值金餘額
  const fetchCustomerBalance = async (customerId) => {
    if (!customerId) return

    setBalanceLoading(true)
    try {
      const response = await api.get(
        `/api/businesses/${businessId}/customers/${customerId}/balance`
      )
      if (response.data.success) {
        setBalanceInfo(response.data.data)
      }
    } catch (error) {
      console.error('獲取儲值金餘額失敗:', error)
      // 如果客戶沒有儲值金記錄，不顯示錯誤訊息
      if (error.response?.status !== 404) {
        antdMessage.error('獲取儲值金餘額失敗')
      }
    } finally {
      setBalanceLoading(false)
    }
  }

  // 獲取儲值金交易歷史
  const fetchTransactionHistory = async (customerId, page = 1) => {
    if (!customerId) return

    setHistoryLoading(true)
    try {
      const response = await api.get(
        `/api/businesses/${businessId}/customers/${customerId}/balance/transactions`,
        {
          params: {
            page,
            limit: 10
          }
        }
      )
      if (response.data.success) {
        setTransactionHistory(response.data.data.transactions)
      }
    } catch (error) {
      console.error('獲取交易歷史失敗:', error)
      antdMessage.error('獲取交易歷史失敗')
    } finally {
      setHistoryLoading(false)
    }
  }

  // 儲值金操作
  const handleBalanceOperation = (operation) => {
    setBalanceOperation(operation)
    setBalanceModalVisible(true)
    balanceForm.resetFields()

    // 如果是調整餘額，重置調整類型
    if (operation === 'adjust') {
      setAdjustmentType('increase')
    }

    // 如果是扣款或退款操作，確保預約記錄已加載
    if (
      (operation === 'deduct' || operation === 'refund') &&
      selectedCustomer?._id &&
      !customerBookings
    ) {
      fetchCustomerBookings(selectedCustomer._id)
    }
  }

  // 提交儲值金操作
  const handleBalanceSubmit = async (values, consentSignatureId = null, consentResolved = false) => {
    if (!selectedCustomer?._id) return
    const sigId = typeof consentSignatureId === 'string' ? consentSignatureId : null

    // 儲值：先走購買同意書（無綁定則直接儲值；現場簽回傳 signatureId）
    if (balanceOperation === 'topup' && !consentResolved) {
      const displayName = getCustomerDisplayData?.(selectedCustomer, businessId)?.name || ''
      setBalanceConsent({
        open: true,
        customerId: selectedCustomer._id,
        customerName: displayName,
        pendingValues: values,
        variables: {
          planName: (values.schemeKey && values.schemeKey.trim()) || '儲值',
          amount: values.amount || 0,
          price: values.amount || 0,
          expiryDate: values.expiryDate
            ? dayjs(values.expiryDate).format('YYYY/MM/DD')
            : '',
          customerName: displayName
        }
      })
      return
    }

    // 送出中不接受第二次：以前這裡完全沒有防連點，確認鈕也沒有 loading，
    // 回應慢一點店員再按一次就會送出兩筆（ref 同步可讀，不受 closure 影響）
    if (balanceSubmittingRef.current) return
    balanceSubmittingRef.current = true
    setBalanceSubmitting(true)

    try {
      let endpoint = ''
      let requestData = {
        amount: values.amount,
        note: values.note || ''
      }

      switch (balanceOperation) {
        case 'topup':
          endpoint = `/api/businesses/${businessId}/customers/${selectedCustomer._id}/balance/topup`
          if (values.schemeKey?.trim())
            requestData.schemeKey = values.schemeKey.trim()
          if (values.expiryDate)
            requestData.expiryDate = dayjs(values.expiryDate).format(
              'YYYY-MM-DD'
            )
          if (values.salesPersonnelId)
            requestData.salesPersonnelId = values.salesPersonnelId
          if (sigId) requestData.consentSignatureId = sigId
          break
        case 'deduct':
          endpoint = `/api/businesses/${businessId}/customers/${selectedCustomer._id}/balance/deduct`
          break
        case 'refund':
          endpoint = `/api/businesses/${businessId}/customers/${selectedCustomer._id}/balance/refund`
          break
        case 'adjust':
          endpoint = `/api/businesses/${businessId}/customers/${selectedCustomer._id}/balance/adjust`
          // 根據調整類型決定金額正負
          requestData.amount =
            adjustmentType === 'increase' ? values.amount : -values.amount
          break
        default:
          return
      }

      // 儲值遇到「可能重複儲值」時先請店家確認；取消就停在視窗讓他檢查
      const response =
        balanceOperation === 'topup'
          ? await postTopupWithDuplicateConfirm(api, endpoint, requestData)
          : await api.post(endpoint, requestData)
      if (!response) return

      if (response.data.success) {
        antdMessage.success(response.data.message || '操作成功')
        setBalanceModalVisible(false)
        balanceForm.resetFields()
        // 重新獲取餘額和交易歷史
        fetchCustomerBalance(selectedCustomer._id)
        fetchTransactionHistory(selectedCustomer._id)
      } else {
        antdMessage.error(response.data.error || '操作失敗')
      }
    } catch (error) {
      console.error('儲值金操作失敗:', error)
      antdMessage.error(error.response?.data?.error || '操作失敗')
    } finally {
      balanceSubmittingRef.current = false
      setBalanceSubmitting(false)
    }
  }

  const renderDrawerContent = () => {
    if (!selectedCustomer) return null

    const stats = selectedCustomer.visitStats || {}
    const displayData = getCustomerDisplayData(selectedCustomer, businessId)

    return (
      <>
        {isUploading && (
          <div className='loading-mask'>
            <Spin size='large' tip='正在上傳照片，請稍候...' />
          </div>
        )}
        <CustomerDetailsWrapper>
          <div className='avatar-section'>
            <AvatarSection>
              <CustomerAvatarImg
                src={getCustomerAvatarSrc(selectedCustomer)}
                name={displayData.name}
                size={120}
                style={{
                  marginBottom: 16,
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                }}
              />
              <CustomerName>
                {displayData.name}
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: '#8e8e93',
                    marginLeft: 6
                  }}
                >
                  {getLineDisplayNameSuffix(selectedCustomer, displayData)}
                </span>
              </CustomerName>
              <MembershipBadge
                color={
                  getMembershipDisplay(
                    displayData.membershipLevel,
                    membershipTiers
                  ).color
                }
              >
                {
                  getMembershipDisplay(
                    displayData.membershipLevel,
                    membershipTiers
                  ).text
                }
              </MembershipBadge>
            </AvatarSection>
          </div>

          <div className='stats-section'>
            <StatsGrid>
              <StatItem>
                <Statistic
                  title='完成預約數'
                  value={stats.completedBookings || 0}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                />
              </StatItem>

              <StatItem>
                <Statistic
                  title='出席率'
                  value={stats.attendanceRate || 0}
                  suffix='%'
                  prefix={<CalendarOutlined style={{ color: '#1890ff' }} />}
                />
              </StatItem>

              <StatItem>
                <Statistic
                  title='平均消費'
                  value={Math.ceil(Number(stats.averageSpending) || 0)}
                  prefix={<DollarOutlined style={{ color: '#faad14' }} />}
                  precision={0}
                  formatter={(value) => `$${value}`}
                />
              </StatItem>

              <StatItem>
                <Statistic
                  title='未出席次數'
                  value={stats.noShowCount || 0}
                  prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                />
              </StatItem>

              <StatItem>
                <Statistic
                  title='取消次數'
                  value={stats.cancelledBookings || 0}
                  prefix={<CloseCircleOutlined style={{ color: '#ff7a45' }} />}
                />
              </StatItem>

              <StatItem>
                <Statistic
                  title='近期回訪'
                  value={
                    formatTz(stats.lastVisitDate, 'MM/DD')
                  }
                  prefix={<CalendarOutlined style={{ color: '#13c2c2' }} />}
                />
              </StatItem>
            </StatsGrid>
          </div>

          <div className='info-section'>
            <InfoSection>
              <InfoItem>
                <InfoLabel>電話</InfoLabel>
                <InfoValue>
                  {isEditing ? (
                    <Input
                      value={editedCustomer.businessSpecificData?.phone || ''}
                      onChange={(e) =>
                        setEditedCustomer({
                          ...editedCustomer,
                          businessSpecificData: {
                            ...editedCustomer.businessSpecificData,
                            phone: e.target.value
                          }
                        })
                      }
                      placeholder='請輸入電話'
                    />
                  ) : (
                    displayData.phone
                  )}
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>郵箱</InfoLabel>
                <InfoValue>
                  {isEditing ? (
                    <Input
                      value={editedCustomer.businessSpecificData?.email || ''}
                      onChange={(e) =>
                        setEditedCustomer({
                          ...editedCustomer,
                          businessSpecificData: {
                            ...editedCustomer.businessSpecificData,
                            email: e.target.value
                          }
                        })
                      }
                      placeholder='請輸入郵箱'
                    />
                  ) : (
                    displayData.email
                  )}
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>姓名</InfoLabel>
                {isEditing ? (
                  <Form.Item
                    style={{ margin: 0, flex: 1 }}
                    validateTrigger='onBlur'
                    validateFirst={true}
                    rules={[{ required: true, message: '請輸入姓名' }]}
                  >
                    <Input
                      value={editedCustomer.businessSpecificData?.name || ''}
                      onChange={(e) =>
                        setEditedCustomer({
                          ...editedCustomer,
                          businessSpecificData: {
                            ...editedCustomer.businessSpecificData,
                            name: e.target.value
                          }
                        })
                      }
                      placeholder='請輸入姓名'
                    />
                  </Form.Item>
                ) : (
                  <InfoValue>{displayData.name}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>性別</InfoLabel>
                {isEditing ? (
                  <Radio.Group
                    value={editedCustomer.businessSpecificData?.gender || ''}
                    onChange={(e) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          gender: e.target.value
                        }
                      })
                    }
                  >
                    <Radio value='male'>男</Radio>
                    <Radio value='female'>女</Radio>
                  </Radio.Group>
                ) : (
                  <InfoValue>{translateGender(displayData.gender)}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>生日</InfoLabel>
                {isEditing ? (
                  <DatePicker
                    value={
                      editedCustomer.businessSpecificData?.birthdate
                        ? dayjs(editedCustomer.businessSpecificData.birthdate)
                        : null
                    }
                    onChange={(date) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          birthdate: date ? date.format('YYYY-MM-DD') : null
                        }
                      })
                    }
                    style={{ width: '100%' }}
                  />
                ) : (
                  <InfoValue>
                    {formatTz(displayData.birthdate, 'YYYY/MM/DD', '未知')}
                  </InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>地址</InfoLabel>
                {isEditing ? (
                  <Input
                    value={editedCustomer.businessSpecificData?.region || ''}
                    onChange={(e) =>
                      setEditedCustomer({
                        ...editedCustomer,
                        businessSpecificData: {
                          ...editedCustomer.businessSpecificData,
                          region: e.target.value
                        }
                      })
                    }
                    placeholder='請輸入地址（選填）'
                  />
                ) : (
                  <InfoValue>{displayData.region || '—'}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>紅利點數</InfoLabel>
                <InfoValue>{displayData.points}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>最後到店</InfoLabel>
                <InfoValue>
                  {formatTz(
                    selectedCustomer.visitStats?.lastCompletedDate,
                    'YYYY/MM/DD',
                    '未知'
                  )}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>總消費</InfoLabel>
                <InfoValue>{displayData.totalSpent}</InfoValue>
              </InfoItem>
              <SectionTitle style={{ marginTop: 24 }}>
                照片記錄
                <Text
                  type='secondary'
                  style={{ fontSize: '14px', marginLeft: 8 }}
                ></Text>
                {plan === 'free' && (
                  <Tag color='#d5728a' style={{ marginLeft: 8 }}>
                    商務品牌功能
                  </Tag>
                )}
              </SectionTitle>
              <PhotoWall style={{ position: 'relative' }}>
                {isEditing ? (
                  plan === 'free' ? (
                    <div style={{ position: 'relative' }}>
                      <Upload
                        listType='picture-card'
                        fileList={[]}
                        disabled={true}
                      >
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>上傳</div>
                        </div>
                      </Upload>
                      <PhotoMask>
                        <LockOutlined
                          style={{ fontSize: '24px', marginBottom: '12px' }}
                        />
                        <UpgradeText>照片管理為商務品牌功能</UpgradeText>
                      </PhotoMask>
                    </div>
                  ) : (
                    <Upload
                      listType='picture-card'
                      fileList={customerPhotos}
                      onChange={handlePhotoChange}
                      beforeUpload={() => false}
                      multiple
                      showUploadList={{
                        showRemoveIcon: true,
                        removeIcon: <DeleteOutlined />,
                        showPreviewIcon: true
                      }}
                    >
                      {customerPhotos.length >= 20 ? null : (
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>上傳</div>
                        </div>
                      )}
                    </Upload>
                  )
                ) : (
                    selectedCustomer.photos ||
                    selectedCustomer.businessSpecificData?.photos ||
                    []
                  ).length > 0 ? (
                  <div style={{ position: 'relative' }}>
                    <Upload
                      listType='picture-card'
                      fileList={(
                        selectedCustomer.photos ||
                        selectedCustomer.businessSpecificData?.photos ||
                        []
                      ).map((photo, index) => ({
                        uid: photo._id,
                        name: `photo-${index}`,
                        status: 'done',
                        url: `${PHOTO_URL}${photo.url}`
                      }))}
                      disabled={true}
                    />
                    {plan === 'free' && (
                      <PhotoMask>
                        <LockOutlined
                          style={{ fontSize: '24px', marginBottom: '12px' }}
                        />
                        <UpgradeText>
                          升級至商務品牌方案，即可管理客戶照片
                        </UpgradeText>
                      </PhotoMask>
                    )}
                  </div>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      selectedCustomer.membershipLevel === 'Regular' ? (
                        <span>照片管理為商務品牌功能</span>
                      ) : (
                        '暫無照片'
                      )
                    }
                    style={{ margin: '20px 0' }}
                  />
                )}
              </PhotoWall>
            </InfoSection>
          </div>

          <div className='additional-info'>
            {/* 儲值金管理區塊 */}
            <BalanceSection>
              <SectionTitle>
                <WalletOutlined style={{ marginRight: 8 }} />
                儲值金管理
              </SectionTitle>

              {balanceLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size='small' />
                  <div style={{ marginTop: 8, color: '#666' }}>載入中...</div>
                </div>
              ) : balanceInfo ? (
                <BalanceInfo>
                  <BalanceCard>
                    <BalanceAmount>
                      NT$ {balanceInfo.balance?.toFixed(2) || '0.00'}
                    </BalanceAmount>
                    <BalanceLabel>當前餘額</BalanceLabel>
                  </BalanceCard>
                  {balanceInfo.balanceByScheme?.length > 0 && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: '8px 0',
                        borderTop: '1px solid #f0f0f0',
                        fontSize: 12,
                        color: '#666'
                      }}
                    >
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>
                        各儲值活動餘額
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {balanceInfo.balanceByScheme.map((s) => (
                          <li key={s.schemeKey} style={{ marginBottom: 2 }}>
                            {s.schemeKey} NT${' '}
                            {Number(s.balance || 0).toLocaleString()}
                            {(s.expiryDateDisplay || s.expiryDate) &&
                              `（期限 ${s.expiryDateDisplay || dayjs(s.expiryDate).format('YYYY/MM/DD')}）`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <BalanceStats>
                    <BalanceStatItem>
                      <StatLabel>累計儲值</StatLabel>
                      <StatValue>
                        NT$ {balanceInfo.totalTopup?.toFixed(2) || '0.00'}
                      </StatValue>
                    </BalanceStatItem>
                    <BalanceStatItem>
                      <StatLabel>累計消費</StatLabel>
                      <StatValue>
                        NT$ {balanceInfo.totalSpent?.toFixed(2) || '0.00'}
                      </StatValue>
                    </BalanceStatItem>
                  </BalanceStats>

                  <BalanceActions>
                    <BalanceButton
                      type='primary'
                      icon={<PlusCircleOutlined />}
                      onClick={() => handleBalanceOperation('topup')}
                    >
                      儲值
                    </BalanceButton>
                    <BalanceButton
                      icon={<MinusCircleOutlined />}
                      onClick={() => handleBalanceOperation('deduct')}
                    >
                      扣款
                    </BalanceButton>
                    <BalanceButton
                      icon={<ReloadOutlined />}
                      onClick={() => handleBalanceOperation('refund')}
                    >
                      退款
                    </BalanceButton>
                    <BalanceButton
                      icon={<EditOutlined />}
                      onClick={() => handleBalanceOperation('adjust')}
                    >
                      調整
                    </BalanceButton>
                  </BalanceActions>
                </BalanceInfo>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description='尚未建立儲值金帳戶'
                  style={{ margin: '20px 0' }}
                >
                  <Button
                    type='primary'
                    icon={<PlusCircleOutlined />}
                    onClick={() => handleBalanceOperation('topup')}
                  >
                    建立儲值金帳戶
                  </Button>
                </Empty>
              )}

              {/* 交易歷史 */}
              {balanceInfo && (
                <TransactionHistory>
                  <SectionTitle>
                    <HistoryOutlined style={{ marginRight: 8 }} />
                    交易記錄
                    <Button
                      type='link'
                      size='small'
                      onClick={() =>
                        fetchTransactionHistory(selectedCustomer._id)
                      }
                      loading={historyLoading}
                    >
                      刷新
                    </Button>
                  </SectionTitle>

                  {historyLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin size='small' />
                    </div>
                  ) : transactionHistory.length > 0 ? (
                    <TransactionList>
                      {transactionHistory.map((transaction) => (
                        <TransactionItem key={transaction._id}>
                          <TransactionInfo>
                            <TransactionType type={transaction.type}>
                              {transaction.type === 'topup' && '儲值'}
                              {transaction.type === 'deduct' && '扣款'}
                              {transaction.type === 'refund' && '退款'}
                              {transaction.type === 'adjustment' && '調整'}
                            </TransactionType>
                            <TransactionAmount amount={transaction.amount}>
                              {transaction.amount > 0 ? '+' : '-'}
                              NT${' '}
                              {Math.abs(transaction.amount).toLocaleString()}
                            </TransactionAmount>
                          </TransactionInfo>
                          <TransactionDetails>
                            <TransactionNote>
                              {transaction.note}
                            </TransactionNote>
                            {transaction.type === 'deduct' &&
                              (transaction.bookingDate ||
                                transaction.bookingTime) && (
                                <TransactionTime>
                                  預約：
                                  {[
                                    transaction.bookingDate,
                                    transaction.bookingTime
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                </TransactionTime>
                              )}
                            <TransactionTime>
                              {dayjs(transaction.createdAt).format(
                                'YYYY/MM/DD HH:mm'
                              )}
                            </TransactionTime>
                            <TransactionOperator>
                              操作者：
                              {transaction.operator?.name || '商家'}
                            </TransactionOperator>
                          </TransactionDetails>
                        </TransactionItem>
                      ))}
                    </TransactionList>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description='暫無交易記錄'
                      style={{ margin: '20px 0' }}
                    />
                  )}
                </TransactionHistory>
              )}
            </BalanceSection>

            <TagsSection>
              <SectionTitle>標籤</SectionTitle>
              {isEditing ? (
                <>
                  <TagHelperText>
                    提示：可直接輸入新標籤，按下 Enter 鍵或逗號(,)添加
                  </TagHelperText>
                  <Select
                    mode='tags'
                    style={{ width: '100%' }}
                    placeholder='選擇現有標籤輸入新標籤'
                    value={displayTags(editedCustomer.tags)}
                    onChange={(newTags) => {
                      // 確保每個標籤都是獨立的字串
                      const processedTags = newTags
                        .map((tag) => tag.trim())
                        .filter((tag) => tag)
                      setEditedCustomer({
                        ...editedCustomer,
                        tags: processedTags
                      })
                    }}
                    tokenSeparators={[',']}
                  >
                    {tagOptions.map((tag) => (
                      <Select.Option key={tag} value={tag}>
                        {tag}
                      </Select.Option>
                    ))}
                  </Select>
                </>
              ) : (
                <TagsWrapper>
                  {displayTags(selectedCustomer.tags).map((tag, index) => (
                    <Tag key={index} color='blue'>
                      {tag}
                    </Tag>
                  ))}
                </TagsWrapper>
              )}
            </TagsSection>
            <NotesSection>
              <SectionTitle>備註</SectionTitle>
              {isEditing ? (
                <Input.TextArea
                  rows={4}
                  value={editedCustomer.notes}
                  onChange={(e) =>
                    setEditedCustomer({
                      ...editedCustomer,
                      notes: e.target.value
                    })
                  }
                />
              ) : (
                <Notes>{selectedCustomer.notes || '無'}</Notes>
              )}
            </NotesSection>
            <MembershipSection>
              <SectionTitle>會員等級</SectionTitle>
              {isEditing ? (
                <Select
                  style={{ width: '100%' }}
                  loading={membershipTiersLoading}
                  value={editedCustomer.membershipLevel}
                  onChange={(level) =>
                    setEditedCustomer({
                      ...editedCustomer,
                      membershipLevel: level
                    })
                  }
                  placeholder='請選擇會員等級'
                  allowClear
                >
                  {/*
                    舊的英文名稱選項 —— 只在店家「還沒建自己的等級表」時才出現。
                    這幾個 Option 的 value 是小寫英文（'vip'），跟店家實際的等級
                    名稱（'VIP會員'）對不上；一旦被選到，這位客人的等級在
                    findTierByName 就永遠查不到 —— 折扣抓不到、到期評估當成
                    「沒有會籍」跳過。萊斯美學仁義店就有一筆 'vip' 是這樣來的。
                    店家有自己的等級時，只給店家自己的等級，不給製造髒資料的機會。
                  */}
                  {membershipTiers.length === 0 && (
                    <>
                      <Select.Option value='Regular'>普通會員</Select.Option>
                      <Select.Option value='Silver'>銀卡會員</Select.Option>
                      <Select.Option value='Gold'>金卡會員</Select.Option>
                      <Select.Option value='Diamond'>鑽石會員</Select.Option>
                      <Select.Option value='VIP'>VIP會員</Select.Option>
                    </>
                  )}
                  {/* 自定義會員等級選項 */}
                  {membershipTiers.map((tier) => (
                    <Select.Option key={tier._id} value={tier.name}>
                      <Tag
                        style={{
                          background: tier.color || '#1890ff',
                          color: tier.color === '#ffd700' ? '#000' : '#fff',
                          border: 'none'
                        }}
                      >
                        {tier.name}
                      </Tag>{' '}
                      {tier.discountRate}%
                      {tier.discountRate === 100
                        ? '（無折扣）'
                        : `（${tier.discountRate}折）`}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                <MembershipBadge
                  color={
                    getMembershipDisplay(
                      selectedCustomer.membershipLevel,
                      membershipTiers
                    ).color
                  }
                >
                  {
                    getMembershipDisplay(
                      selectedCustomer.membershipLevel,
                      membershipTiers
                    ).text
                  }
                </MembershipBadge>
              )}
            </MembershipSection>

            {/* <BlacklistSection>
              <SectionTitle>黑名單</SectionTitle>
              <Space>
                <Switch
                  checked={
                    isEditing
                      ? editedCustomer.blacklist
                      : selectedCustomer.blacklist
                  }
                  onChange={(checked) => {
                    if (isEditing) {
                      setEditedCustomer({
                        ...editedCustomer,
                        blacklist: checked
                      })
                    }
                  }}
                  disabled={!isEditing}
                />
                <span>
                  {isEditing
                    ? editedCustomer.blacklist
                      ? '開啟'
                      : '關閉'
                    : selectedCustomer.blacklist
                    ? '開啟'
                    : '關閉'}
                </span>
              </Space>
            </BlacklistSection> */}
            {stats.frequentServices && stats.frequentServices.length > 0 && (
              <>
                <SectionTitle>常做項目</SectionTitle>
                <ServiceList>
                  {stats.frequentServices?.map((service) => (
                    <div key={service.productId} className='service-item'>
                      <Text strong>{service.productName}</Text>
                      <Tag color='blue'>{service.count} 次</Tag>
                    </div>
                  ))}
                </ServiceList>
              </>
            )}

            {selectedCustomer.latestBooking && (
              <LatestBookingSection>
                <SectionTitle>最新預約</SectionTitle>
                <BookingInfo>
                  <BookingItem>
                    <BookingLabel>項目</BookingLabel>
                    <BookingValue>
                      {selectedCustomer.latestBooking.productName}
                    </BookingValue>
                  </BookingItem>
                  <BookingItem>
                    <BookingLabel>操作人員</BookingLabel>
                    <BookingValue>
                      {selectedCustomer.latestBooking.stylistName || '待指派'}
                    </BookingValue>
                  </BookingItem>
                  <BookingItem>
                    <BookingLabel>日期</BookingLabel>
                    <BookingValue>
                      {/* booking.date 是牆上日期字串，不是絕對時刻：
                          new Date('2026-09-15') 會被當成 UTC 午夜，在 UTC 以西的
                          裝置上顯示成前一天。dayjs 解析裸日期是本地午夜，格式化回去
                          任何時區結果都一樣 */}
                      {dayjs(selectedCustomer.latestBooking.date).format(
                        'YYYY/MM/DD'
                      )}
                    </BookingValue>
                  </BookingItem>
                  <BookingItem>
                    <BookingLabel>時間</BookingLabel>
                    <BookingValue>
                      {selectedCustomer.latestBooking.time}
                    </BookingValue>
                  </BookingItem>
                </BookingInfo>
              </LatestBookingSection>
            )}
          </div>
        </CustomerDetailsWrapper>

        <SectionTitle style={{ marginTop: 24 }}>
          預約記錄
          <Button
            type='link'
            onClick={() => setShowBookingDrawer(true)}
            style={{ marginLeft: 8 }}
          >
            查看全部
          </Button>
        </SectionTitle>
      </>
    )
  }

  if (loading) {
    return (
      <Spin
        size='large'
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '100px'
        }}
      />
    )
  }

  return (
    <Container>
      <SearchContainer>
        <SearchRow>
          <StyledSearchInput
            placeholder='搜尋姓名、電話、ID（輸入後按 Enter 搜尋）'
            value={searchTerm}
            onChange={handleSearchChange}
            allowClear
            onPressEnter={(e) => {
              e.preventDefault()
              handleSearch()
            }}
          />
          <SearchButton
            onClick={(e) => {
              e.preventDefault()
              handleSearch()
            }}
            icon={<SearchOutlined />}
          >
            搜索
          </SearchButton>
        </SearchRow>
        {/* 進階搜索按鈕（電腦版：搜索框右側；手機版用 FAB） */}
        <AdvancedSearchDesktopBtn
          type='button'
          className={filterType === 'advanced' && activeAdvancedCount > 0 ? 'active' : ''}
          onClick={openAdvancedSearch}
        >
          <SlidersOutlined />
          進階搜索
          {filterType === 'advanced' && activeAdvancedCount > 0 && (
            <span className='adv-badge'>{activeAdvancedCount}</span>
          )}
        </AdvancedSearchDesktopBtn>
        <FilterSegmentWrap>
          <FilterSegmentButton
            type='button'
            className={filterType === 'all' ? 'active' : ''}
            onClick={handleFilterChange}
          >
            全部客戶
          </FilterSegmentButton>
          <FilterSegmentButton
            type='button'
            className={filterType === 'birthday' ? 'active' : ''}
            onClick={handleBirthdayFilter}
          >
            <GiftOutlined />
            當月壽星
          </FilterSegmentButton>
          <FilterSegmentButton
            type='button'
            className={filterType === 'tier' ? 'active' : ''}
            onClick={() => setTierFilterModalOpen(true)}
          >
            <FilterOutlined />
            {filterType === 'tier' && tierFilter ? `會員等級：${tierFilter}` : '會員等級'}
          </FilterSegmentButton>
          {filterType === 'tier' && tierFilter && (
            <FilterSegmentButton
              type='button'
              onClick={handleClearTierFilter}
              style={{ color: '#d5728a' }}
            >
              清除
            </FilterSegmentButton>
          )}
          <FilterSegmentButton
            type='button'
            className={showRegion ? 'active' : ''}
            onClick={() => {
              const next = !showRegion
              setShowRegion(next)
              localStorage.setItem('customersShowRegion', next ? '1' : '0')
            }}
          >
            {showRegion ? '隱藏地址' : '顯示地址'}
          </FilterSegmentButton>
        </FilterSegmentWrap>
      </SearchContainer>
      {searchLoading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 0'
          }}
        >
          <Spin size='large' tip='搜索中...' />
        </div>
      )}
      <TableWrapper>
        {customers.length > 0 ? (
          <CustomerCardsContainer>
            {customers.map((customer) => (
              <CustomerCardComponent
                key={customer._id}
                customer={customer}
                onShowModal={showDrawer}
                membershipTiers={membershipTiers}
                businessId={businessId}
                showRegion={showRegion}
              />
            ))}
          </CustomerCardsContainer>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
              color: '#86868b',
              fontSize: '16px'
            }}
          >
            <Empty description='暫無客戶數據' />
          </div>
        )}
      </TableWrapper>
      <PaginationWrapper>
        <PaginationTotal>共 {totalCustomers} 筆資料</PaginationTotal>
        <StyledPagination
          current={currentPage}
          total={totalCustomers}
          pageSize={pageSize}
          onChange={handlePageChange}
          showSizeChanger={false}
          showQuickJumper
        />
      </PaginationWrapper>
      <StyledDrawer
        title={<DrawerTitle>客戶詳情</DrawerTitle>}
        placement='right'
        closable={true}
        onClose={onClose}
        open={visible}
        width={window.innerWidth > 820 ? 520 : '100%'}
        extra={
          <Space>
            {isEditing ? (
              <>
                <Button onClick={handleCancel} disabled={isUploading}>
                  取消
                </Button>
                <Button
                  type='primary'
                  onClick={handleSave}
                  loading={isUploading}
                  disabled={isUploading}
                >
                  保存
                </Button>
              </>
            ) : (
              <>
                <Button
                  danger
                  onClick={() => handleDelete(selectedCustomer?._id)}
                  icon={<DeleteOutlined />}
                  disabled={!selectedCustomer || isUploading}
                >
                  刪除
                </Button>
                <Button
                  type='primary'
                  onClick={handleEdit}
                  icon={<EditOutlined />}
                  disabled={isUploading}
                >
                  編輯
                </Button>
              </>
            )}
          </Space>
        }
        footer={
          <div style={{ textAlign: 'center' }}>
            <Button
              type='primary'
              onClick={() => handleViewBookings(selectedCustomer._id)}
              icon={<CalendarOutlined />}
              style={{ width: '90%', maxWidth: '400px' }}
            >
              查看預約紀錄
            </Button>
          </div>
        }
      >
        {renderDrawerContent()}
      </StyledDrawer>
      <Drawer
        title='新增顧客'
        placement='right'
        width='100%'
        styles={{ body: { height: '100%' } }}
        onClose={() => setAddDrawerVisible(false)}
        open={addDrawerVisible}
      >
        <Form
          form={addCustomerForm}
          layout='vertical'
          onFinish={handleAddCustomer}
        >
          <Form.Item
            name='name'
            label='姓名'
            rules={[{ required: true, message: '請輸入姓名' }]}
          >
            <Input placeholder='請輸入顧客姓名' />
          </Form.Item>

          <Form.Item
            name='phone'
            label='電話'
            rules={[
              {
                validator: (_, value) => {
                  // 如果沒有輸入，則通過驗證（選填）
                  if (!value || value.trim() === '') {
                    return Promise.resolve()
                  }
                  // 如果有輸入，則驗證格式
                  if (/^09\d{8}$/.test(value)) {
                    return Promise.resolve()
                  }
                  return Promise.reject(
                    new Error('請輸入正確的手機號碼（09開頭的10位數字）')
                  )
                }
              }
            ]}
          >
            <Input placeholder='請輸入顧客電話(09開頭的10位數字)' />
          </Form.Item>

          <Form.Item
            name='email'
            label='電子郵件'
            rules={[{ type: 'email', message: '請輸入有效的電子郵件' }]}
          >
            <Input placeholder='請輸入電子郵件' />
          </Form.Item>

          <Form.Item name='gender' label='性別'>
            <Radio.Group>
              <Radio value='male'>男</Radio>
              <Radio value='female'>女</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name='birthdate' label='生日'>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name='tags'
            label='標籤'
            extra='可選擇現有標籤或直接輸入新標籤，按下 Enter 鍵或逗號(,)添加'
          >
            <Select
              mode='tags'
              style={{ width: '100%' }}
              placeholder='選擇現有標籤或輸入新標籤'
              tokenSeparators={[',']}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ padding: '0 8px 4px' }}>
                    <Typography.Text type='secondary'>
                      已有標籤供選擇，或直接輸入創建新標籤
                    </Typography.Text>
                  </div>
                </>
              )}
            >
              {tagOptions.map((tag) => (
                <Select.Option key={tag} value={tag}>
                  {tag}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name='region' label='地址'>
            <Input placeholder='請輸入地址（選填，僅店家可見）' />
          </Form.Item>

          <Form.Item name='notes' label='備註'>
            <Input.TextArea rows={4} placeholder='請輸入備註' />
          </Form.Item>

          <Form.Item>
            <Button type='primary' htmlType='submit' block>
              新增顧客
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
      <BookingDrawer
        title={`${
          selectedCustomer
            ? getCustomerDisplayData(selectedCustomer, businessId).name
            : '客戶'
        }的預約記錄`}
        placement='right'
        width='100%'
        onClose={() => setBookingsVisible(false)}
        open={bookingsVisible}
      >
        <BookingTable
          isCustomerView={true}
          customerInfo={selectedCustomer}
          onPageChange={(page) =>
            fetchCustomerBookings(selectedCustomer?._id, page)
          }
          bookings={customerBookings}
          loading={bookingsLoading}
        />
      </BookingDrawer>

      {/* 購買同意書（儲值前簽署） */}
      <PurchaseConsentModal
        open={balanceConsent.open}
        onClose={() => setBalanceConsent((c) => ({ ...c, open: false }))}
        businessId={businessId}
        customerId={balanceConsent.customerId}
        customerName={balanceConsent.customerName}
        target='balance'
        variables={balanceConsent.variables}
        onNoForm={() => {
          setBalanceConsent((c) => ({ ...c, open: false }))
          handleBalanceSubmit(balanceConsent.pendingValues, null, true)
        }}
        onSkip={() => {
          setBalanceConsent((c) => ({ ...c, open: false }))
          handleBalanceSubmit(balanceConsent.pendingValues, null, true)
        }}
        onSigned={(sigId) => {
          setBalanceConsent((c) => ({ ...c, open: false }))
          handleBalanceSubmit(balanceConsent.pendingValues, sigId, true)
        }}
        onSentRemote={() => {
          setBalanceConsent((c) => ({ ...c, open: false }))
          handleBalanceSubmit(balanceConsent.pendingValues, null, true)
        }}
      />

      {/* 儲值金操作Modal */}
      <Modal
        title={
          (balanceOperation === 'topup' && '儲值') ||
          (balanceOperation === 'deduct' && '扣款') ||
          (balanceOperation === 'refund' && '退款') ||
          (balanceOperation === 'adjust' && '調整餘額') ||
          '儲值金操作'
        }
        open={balanceModalVisible}
        onCancel={() => setBalanceModalVisible(false)}
        onOk={() => balanceForm.submit()}
        confirmLoading={balanceSubmitting}
        okText='確認'
        cancelText='取消'
        width={500}
      >
        <Form
          form={balanceForm}
          layout='vertical'
          onFinish={handleBalanceSubmit}
        >
          {balanceOperation === 'topup' && (
            <>
              <Form.Item
                name='salesPersonnelId'
                label='銷售人員（選填，有選才會列入儲值紀錄／業績／抽成）'
              >
                <Select
                  placeholder='選擇銷售人員'
                  allowClear
                  style={{ width: '100%' }}
                  options={employees.map((e) => ({
                    value: e._id,
                    label: e.name
                  }))}
                />
              </Form.Item>
              <Form.Item name='schemeKey' label='儲值活動（選填）'>
                <TopupSchemeKeyInput
                  businessId={businessId}
                  placeholder='可輸入或從下拉選擇曾用過的儲值活動'
                  extraSchemeKeys={
                    balanceInfo?.balanceByScheme
                      ?.map((s) => s.schemeKey)
                      .filter(Boolean) ?? []
                  }
                />
              </Form.Item>
              <Form.Item name='expiryDate' label='使用期限（選填）'>
                <DatePicker
                  style={{ width: '100%' }}
                  format='YYYY-MM-DD'
                  placeholder='選擇使用期限'
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name='amount'
            label={balanceOperation === 'adjust' ? '調整金額' : '金額'}
            rules={[
              { required: true, message: '請輸入金額' },
              { type: 'number', min: 1, message: '金額必須大於等於1元' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={
                balanceOperation === 'adjust' ? '請輸入調整金額' : '請輸入金額'
              }
              precision={0}
              min={1}
              step={1}
              formatter={(value) =>
                `NT$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value.replace(/NT\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          {balanceOperation === 'adjust' && (
            <Form.Item label='調整類型'>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  type={adjustmentType === 'increase' ? 'primary' : 'default'}
                  onClick={() => setAdjustmentType('increase')}
                  style={{ flex: 1, height: '40px' }}
                  icon={<PlusCircleOutlined />}
                >
                  增加餘額
                </Button>
                <Button
                  type={adjustmentType === 'decrease' ? 'primary' : 'default'}
                  onClick={() => setAdjustmentType('decrease')}
                  style={{ flex: 1, height: '40px' }}
                  icon={<MinusCircleOutlined />}
                >
                  減少餘額
                </Button>
              </div>
              <div
                style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor:
                    adjustmentType === 'increase' ? '#f6ffed' : '#fff2e8',
                  border: `1px solid ${
                    adjustmentType === 'increase' ? '#b7eb8f' : '#ffd591'
                  }`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: adjustmentType === 'increase' ? '#52c41a' : '#fa8c16'
                }}
              >
                {adjustmentType === 'increase'
                  ? '✅ 將為客戶增加儲值金餘額'
                  : '⚠️ 將從客戶儲值金餘額中扣除'}
              </div>
            </Form.Item>
          )}

          {(balanceOperation === 'deduct' || balanceOperation === 'refund') && (
            <Form.Item name='bookingId' label='關聯預約記錄'>
              <Select
                placeholder='請選擇要關聯的預約記錄（選填）'
                allowClear
                showSearch
                optionFilterProp='children'
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(bookingId) => {
                  if (bookingId && customerBookings?.bookings) {
                    const selectedBooking = customerBookings.bookings.find(
                      (booking) => booking._id === bookingId
                    )
                    if (selectedBooking) {
                      const bookingInfo = `${dayjs(selectedBooking.date).format(
                        'YYYY/MM/DD'
                      )} ${selectedBooking.time} - ${
                        selectedBooking.productName
                      } - NT$${
                        selectedBooking.price ||
                        selectedBooking.discountedPrice ||
                        0
                      } (${selectedBooking.status})`
                      const currentNote =
                        balanceForm.getFieldValue('note') || ''
                      const newNote = currentNote
                        ? `${currentNote}\n關聯預約: ${bookingInfo}`
                        : `關聯預約: ${bookingInfo}`
                      balanceForm.setFieldsValue({ note: newNote })
                    }
                  } else {
                    // 如果清空選擇，移除關聯預約信息
                    const currentNote = balanceForm.getFieldValue('note') || ''
                    const newNote = currentNote
                      .replace(/\n關聯預約:.*$/, '')
                      .trim()
                    balanceForm.setFieldsValue({ note: newNote })
                  }
                }}
              >
                {customerBookings?.bookings?.slice(0, 10).map((booking) => (
                  <Select.Option key={booking._id} value={booking._id}>
                    {`${dayjs(booking.date).format('YYYY/MM/DD')} ${
                      booking.time
                    } - ${booking.productName} - NT$${
                      booking.price || booking.discountedPrice || 0
                    } (${booking.status})`}
                  </Select.Option>
                ))}
                {(!customerBookings?.bookings ||
                  customerBookings.bookings.length === 0) && (
                  <Select.Option disabled value='no-bookings'>
                    暫無預約記錄
                  </Select.Option>
                )}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name='note'
            label='備註'
            rules={[
              balanceOperation === 'adjust'
                ? { required: true, message: '調整餘額必須填寫備註說明' }
                : {},
              { max: 200, message: '備註不能超過200字' }
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder={
                balanceOperation === 'adjust'
                  ? '請說明調整原因（必填）'
                  : '請輸入操作備註（選填）'
              }
              maxLength={200}
              showCount
            />
          </Form.Item>

          {balanceOperation === 'adjust' && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: '6px',
                marginBottom: '16px'
              }}
            >
              <Text type='warning'>
                ⚠️ 手動調整功能請謹慎使用，建議僅在系統錯誤或特殊情況下使用
              </Text>
            </div>
          )}
        </Form>
      </Modal>

      {/* 進階搜索 FAB（手機 / 平板，在新增按鈕上方） */}
      <FabAdvancedSearch
        type='button'
        className={filterType === 'advanced' && activeAdvancedCount > 0 ? 'active' : ''}
        onClick={openAdvancedSearch}
        title='進階搜索'
        aria-label='進階搜索'
      >
        <SlidersOutlined />
        {filterType === 'advanced' && activeAdvancedCount > 0 && (
          <span className='fab-badge'>{activeAdvancedCount}</span>
        )}
      </FabAdvancedSearch>

      <FabAddCustomer
        type='button'
        onClick={() => setAddDrawerVisible(true)}
        title='新增顧客'
        aria-label='新增顧客'
      >
        <PlusOutlined />
      </FabAddCustomer>

      {/* 進階搜索 Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SlidersOutlined style={{ color: '#d5728a' }} />
            進階搜索
          </div>
        }
        placement={typeof window !== 'undefined' && window.innerWidth <= 768 ? 'bottom' : 'right'}
        open={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
        width={360}
        height='auto'
        destroyOnClose={false}
        styles={{ body: { paddingBottom: 80 } }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={handleClearAdvancedFilters}>清除篩選</Button>
            <Button
              type='primary'
              onClick={() => advancedForm.submit()}
              style={{ background: '#d5728a', borderColor: '#d5728a' }}
            >
              套用篩選
            </Button>
          </div>
        }
      >
        <Form
          form={advancedForm}
          layout='vertical'
          onFinish={handleApplyAdvancedFilters}
          initialValues={{ gender: '' }}
        >
          {/* 標籤 */}
          <Form.Item name='tags' label='標籤'>
            <Select
              mode='tags'
              placeholder='輸入標籤後按 Enter（可多個）'
              allowClear
              options={tagOptions.map((t) => ({ value: t, label: t }))}
              style={{ width: '100%' }}
            />
          </Form.Item>

          {/* 性別 */}
          <Form.Item name='gender' label='性別'>
            <Radio.Group>
              <Radio value=''>全部</Radio>
              <Radio value='female'>女</Radio>
              <Radio value='male'>男</Radio>
            </Radio.Group>
          </Form.Item>

          {/* 生日月份 */}
          <Form.Item name='birthdayMonth' label='生日月份'>
            <Select placeholder='選擇月份（不限月份留空）' allowClear style={{ width: '100%' }}>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                <Select.Option key={m} value={m}>{m} 月</Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* 會員等級 */}
          {membershipTiers.length > 0 && (
            <Form.Item name='membershipLevel' label='會員等級'>
              <Select placeholder='不限等級' allowClear style={{ width: '100%' }}>
                {membershipTiers.map((t) => (
                  <Select.Option key={t._id} value={t.name}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: t.color || '#888', flexShrink: 0
                        }}
                      />
                      {t.name}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* 最後到店（我的建議項目） */}
          <Form.Item
            name='lastVisitBeforeDays'
            label='最後到店'
            extra='篩選超過指定天數未到店的顧客，適合找沉睡客戶'
          >
            <Select placeholder='不限到店時間' allowClear style={{ width: '100%' }}>
              <Select.Option value={30}>超過 30 天未到店</Select.Option>
              <Select.Option value={60}>超過 60 天未到店</Select.Option>
              <Select.Option value={90}>超過 3 個月未到店</Select.Option>
              <Select.Option value={180}>超過 6 個月未到店</Select.Option>
              <Select.Option value={365}>超過 1 年未到店</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Drawer>

      {/* 會員等級篩選 Modal */}
      <Modal
        open={tierFilterModalOpen}
        title='依會員等級篩選'
        onCancel={() => setTierFilterModalOpen(false)}
        footer={null}
        width={420}
        destroyOnClose
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            paddingTop: 8
          }}
        >
          {membershipTiers.length === 0 ? (
            <Empty description='尚未建立會員等級' />
          ) : (
            <>
              {membershipTiers.map((tier) => (
                <button
                  key={tier._id}
                  type='button'
                  onClick={() => handleApplyTierFilter(tier.name)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 10,
                    border:
                      tierFilter === tier.name
                        ? '2px solid #d5728a'
                        : '1px solid #e6e8ec',
                    background:
                      tierFilter === tier.name ? '#fff7fa' : '#fff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%'
                  }}
                >
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: tier.color || '#d5728a',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff'
                    }}
                  >
                    <CrownOutlined style={{ fontSize: 14 }} />
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>
                      {tier.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      折扣：{tier.discountRate}%
                      {tier.discountRate === 100
                        ? '（無折扣）'
                        : `（${tier.discountRate}折）`}
                    </div>
                  </div>
                </button>
              ))}
              {tierFilter && (
                <button
                  type='button'
                  onClick={() => {
                    handleClearTierFilter()
                    setTierFilterModalOpen(false)
                  }}
                  style={{
                    marginTop: 8,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: '1px dashed #d5728a',
                    background: '#fff',
                    color: '#d5728a',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  清除篩選
                </button>
              )}
            </>
          )}
        </div>
      </Modal>
    </Container>
  )
}

const Container = styled.div`
  padding: 0 0 90px 0;
  @media (max-width: ${breakpoints.medium}) {
    padding: 10px 10px 90px 10px;
  }
`

const TableWrapper = styled.div`
  @media (max-width: ${breakpoints.medium}) {
    border: none;
    background-color: transparent;
  }
`

const Cell = styled.div`
  padding: 10px;
  width: ${(props) => props.width || 'auto'};
  display: flex;
  align-items: center;

  @media (max-width: ${breakpoints.medium}) {
    width: 100%;
    padding: 8px 0;
    &:not(:last-child) {
      border-bottom: 1px solid #f0f0f0;
    }
    &:before {
      content: attr(data-label);
      width: 40%;
      font-weight: bold;
      color: #888;
    }
  }
`

const CustomerInfoOld = styled.div`
  display: flex;
  align-items: center;
`

const CustomerDetails = styled.div`
  font-size: 12px;
  color: #666;
`

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
    overflow: hidden;
    position: relative;
  }

  .loading-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  @media (min-width: 821px) and (max-width: 1024px) {
    .ant-drawer-content-wrapper {
      width: 720px !important;
    }
  }
`
const BookingDrawer = styled(Drawer)`
  &.ant-drawer {
    z-index: 1050;

    .ant-drawer-content-wrapper {
      @media (max-width: 820px) {
        width: 100% !important;
      }
    }

    .ant-drawer-body {
      height: calc(100% - 55px);
      overflow: auto;
      padding: 24px;

      @media (max-width: 820px) {
        padding: 16px;
      }
    }

    .ant-drawer-content {
      height: 100%;
      overflow: hidden;
    }

    .ant-drawer-header {
      position: sticky;
      top: 0;
      z-index: 1;
      background: white;
    }
  }
`

const DrawerTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 0;
`

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`

const CustomerName = styled.h3`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 8px;
`

const MembershipBadge = styled.span`
  background: ${(props) => props.color};
  color: #fff;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
`

const InfoSection = styled.div`
  margin: 16px 0;
  @media (max-width: 820px) {
    margin: 16px;
  }
  @media (max-width: 520px) {
    margin: 16px 0;
  }
`

const InfoItem = styled.div`
  display: flex;
  margin-bottom: 12px;
`

const InfoLabel = styled.span`
  font-weight: bold;
  font-size: 16px;
  width: 100px;
  color: #666;
`

const InfoValue = styled.span`
  flex: 1;
`

const TagsSection = styled.div`
  margin-bottom: 24px;
`

const SectionTitle = styled.h4`
  font-size: 18px;
  margin-top: 0px;
  margin-bottom: 12px;
  color: #333;
`

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Tag = styled.span`
  background-color: #f0f0f0;
  color: #333;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
`

const NotesSection = styled.div`
  margin-bottom: 24px;
`

const Notes = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #666;
`

const LatestBookingSection = styled.div`
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
`

const BookingInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`

const BookingItem = styled.div`
  display: flex;
  flex-direction: column;
`

const BookingLabel = styled.span`
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
`

const BookingValue = styled.span`
  font-size: 14px;
  font-weight: bold;
`

const PaginationWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px 20px;
  width: 100%;
  margin-top: 24px;
  padding: 12px 8px 8px;
  box-sizing: border-box;

  @media (min-width: 769px) {
    justify-content: space-between;
    padding-left: 0;
    padding-right: 0;
  }
`

const PaginationTotal = styled.div`
  flex: 0 0 auto;
  font-size: 14px;
  color: #666;
  line-height: 32px;
  white-space: nowrap;
  width: 100%;
  text-align: center;

  @media (min-width: 769px) {
    width: auto;
    text-align: left;
  }
`

const StyledPagination = styled(Pagination)`
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  column-gap: 6px;
  row-gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;

  @media (min-width: 769px) {
    justify-content: flex-end;
  }

  .ant-pagination-item-link {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ant-pagination-item {
    min-width: 36px;
    height: 36px;
    line-height: 34px;
    margin-inline-end: 0;
    border-radius: 8px;
    border: 1px solid #e5e5ea;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;

    a {
      color: #333;
      padding: 0 6px;
    }
  }

  .ant-pagination-prev,
  .ant-pagination-next,
  .ant-pagination-jump-prev,
  .ant-pagination-jump-next {
    min-width: 36px;
    height: 36px;
    line-height: 34px;
    margin-inline-end: 0;
    border-radius: 8px;
    border: 1px solid #e5e5ea;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ant-pagination-item-active {
    background-color: #d5728a;
    border-color: #d5728a;
    a {
      color: #fff;
    }
  }

  .ant-pagination-disabled .ant-pagination-item-link {
    color: #bfbfbf;
    border-color: #f0f0f0;
    background: #fafafa;
  }

  .ant-pagination-options {
    margin-left: 12px;
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ant-pagination-options-quick-jumper {
    font-size: 14px;
    color: #666;
    white-space: nowrap;

    input {
      border-radius: 8px;
      margin: 0 6px;
    }
  }
`

const MembershipSection = styled.div`
  margin-bottom: 24px;
`

const BlacklistSection = styled.div`
  margin-bottom: 24px;
`

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  gap: 12px;

  @media (min-width: 769px) {
    flex-direction: row;
    align-items: center;
  }
`

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`

const StyledSearchInput = styled(Input)`
  flex: 1;
  border-radius: 20px;
  font-size: 16px; /* 防止 iOS 自動縮放 */

  /* allowClear 會把 input 包進 .ant-input-affix-wrapper，
     內距改由外層負責，避免內外都加而變成雙重留白 */
  &.ant-input-affix-wrapper {
    border-radius: 20px;
    padding: 8px 16px;

    @media (max-width: 768px) {
      padding: 12px 16px;
    }

    .ant-input {
      padding: 0;
      min-height: auto;
      border-radius: 0;
    }
  }

  .ant-input {
    border-radius: 20px;
    font-size: 16px; /* 防止 iOS 自動縮放 */
    padding: 8px 16px;
    height: auto;
    min-height: 40px;

    @media (max-width: 768px) {
      font-size: 16px; /* 保持 16px 防止縮放 */
      padding: 12px 16px;
      min-height: 48px; /* 增加觸控區域 */
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px; /* 防止 iOS 自動縮放 */
  }
`

const SearchButton = styled(Button)`
  border-radius: 20px;
  background-color: #d5728a;
  border-color: #d5728a;
  color: white;
  &:hover,
  &:focus {
    background-color: #d5728a !important;
    border-color: #d5728a !important;
    color: #fff !important;
  }
  @media (max-width: 768px) {
    width: 100%;
  }
`

const AllCustomersButton = styled(Button)`
  border-radius: 20px;
  &.ant-btn-primary {
    background-color: #d5728a;
    border-color: #d5728a;
    &:hover,
    &:focus {
      background-color: #d5728a !important;
      border-color: #d5728a !important;
    }
  }
`

const AddButton = styled(Button)`
  background-color: #fff;
  color: #363233;
  font-size: 14px;
  border-color: #e14a63;
  &:hover,
  &:focus {
    background-color: #fff !important;
    color: #d5728a !important;
    border-color: #e14a63;
  }
`

/* 全部客戶 | 當月壽星 分段控制（手機與電腦一致） */
const FilterSegmentWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: #f0f0f2;
  border-radius: 12px;
  padding: 4px;
  border: 1px solid #e5e5e7;
  width: 100%;
  max-width: 360px;

  @media (max-width: 768px) {
    max-width: none;
  }
`

const FilterSegmentButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 9px 10px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6e6e73;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.2s,
    color 0.2s,
    box-shadow 0.2s;

  .anticon {
    font-size: 13px;
  }

  &:hover {
    color: #1d1d1f;
  }

  &.active {
    color: #fff;
    background: linear-gradient(135deg, #d5728a, #c45f77);
    box-shadow: 0 1px 4px rgba(213, 114, 138, 0.35);
  }

  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 13px;
  }

  @media (max-width: 375px) {
    padding: 9px 6px;
    font-size: 12px;
    gap: 3px;

    .anticon {
      font-size: 12px;
    }
  }
`

/* 進階搜索：電腦版內嵌按鈕（手機隱藏，用 FAB 代替） */
const AdvancedSearchDesktopBtn = styled.button`
  display: none;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid #d5728a;
  background: #fff;
  color: #d5728a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s;

  &:hover {
    background: #fef0f3;
  }

  &.active {
    background: linear-gradient(135deg, #d5728a, #c45f77);
    color: #fff;
    border-color: transparent;
  }

  .adv-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    padding: 0 4px;
    background: #ff4d4f;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    margin-left: 2px;
  }

  @media (min-width: 769px) {
    display: flex;
  }
`

/* 進階搜索：手機 / 平板 FAB（在新增顧客按鈕上方） */
const FabAdvancedSearch = styled.button`
  position: fixed;
  bottom: 90px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1.5px solid #d5728a;
  background: #fff;
  color: #d5728a;
  font-size: 20px;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  z-index: 999;
  transition: all 0.2s;

  &.active {
    background: linear-gradient(135deg, #d5728a, #c45f77);
    color: #fff;
    border-color: transparent;
    box-shadow: 0 4px 16px rgba(213, 114, 138, 0.45);
  }

  .fab-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ff4d4f;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #fff;
  }

  @media (max-width: 768px) {
    display: flex;
    bottom: 88px;
    right: 20px;
    width: 44px;
    height: 44px;
    font-size: 18px;
  }
`

/* 新增顧客：固定右下角懸浮按鈕（與手機版一致） */
const FabAddCustomer = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #d5728a, #c45f77);
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(213, 114, 138, 0.45);
  z-index: 1000;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(213, 114, 138, 0.5);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 52px;
    height: 52px;
    font-size: 26px;
  }
`

// 儲值金相關樣式組件
const BalanceSection = styled.div`
  margin-bottom: 24px;
  background: #fafafa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
`

const BalanceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const BalanceCard = styled.div`
  background: linear-gradient(135deg, #d5728a, #c45f77);
  color: white;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(213, 114, 138, 0.3);
`

const BalanceAmount = styled.div`
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const BalanceLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
`

const BalanceStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`

const BalanceStatItem = styled.div`
  background: white;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e8e8e8;
`

const StatLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`

const StatValue = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #333;
`

const BalanceActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`

const BalanceButton = styled(Button)`
  height: 40px;
  border-radius: 6px;
  font-weight: 500;

  &.ant-btn-primary {
    background-color: #d5728a;
    border-color: #d5728a;
    &:hover {
      background-color: #c45f77;
      border-color: #c45f77;
    }
  }

  &:not(.ant-btn-primary) {
    border-color: #d5728a;
    color: #d5728a;
    &:hover {
      border-color: #c45f77;
      color: #c45f77;
    }
  }
`

const TransactionHistory = styled.div`
  margin-top: 20px;
`

const TransactionList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: white;
`

const TransactionItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #fafafa;
  }
`

const TransactionInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const TransactionType = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => {
    switch (props.type) {
      case 'topup':
        return '#f6ffed'
      case 'deduct':
        return '#fff2e8'
      case 'refund':
        return '#f6ffed'
      case 'adjustment':
        return '#f0f0f0'
      default:
        return '#f0f0f0'
    }
  }};
  color: ${(props) => {
    switch (props.type) {
      case 'topup':
        return '#52c41a'
      case 'deduct':
        return '#fa8c16'
      case 'refund':
        return '#52c41a'
      case 'adjustment':
        return '#666'
      default:
        return '#666'
    }
  }};
`

const TransactionAmount = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: ${(props) => {
    if (props.amount > 0) return '#52c41a'
    if (props.amount < 0) return '#ff4d4f'
    return '#666'
  }};
`

const TransactionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TransactionNote = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`

const TransactionTime = styled.div`
  font-size: 12px;
  color: #999;
`

const TransactionOperator = styled.div`
  font-size: 12px;
  color: #666;
`

export default Customers
